<template>
    <div class="listing-settings-header">
        <CompassInput
            v-if="searchable"
            class="listing-settings-header__utils-search"
            :value="searchValue"
            :placeholder="$t.__('common.labels.search')"
            prefixIcon="search"
            @update:value="updateSearchValue"
        >
            <template #suffix>
                <CompassIconButton
                    v-show="searchValue.length > 0"
                    class="listing-settings-header__utils-search-reset"
                    size="small"
                    icon="close"
                    @click="updateSearchValue('')"
                />
            </template>
        </CompassInput>

        <div v-if="searchable && filters.length > 0" class="listing-settings-header__divider" />

        <div v-if="filters.length > 0" class="listing-settings-header__filters-container">
            <div v-show="hasActiveFilters" class="listing-settings-header__filters-tags">
                <slot name="filters" />
            </div>

            <CompassMenu
                trigger="click"
                class="listing-settings-header__filters-select"
                customClass="listing-settings-header__filters-select-dropdown"
            >
                <CompassPlainButton
                    :label="$t.__('common.labels.add_filter')"
                    variant="neutral"
                    leftIcon="plus"
                />

                <template #dropdown>
                    <CompassMenuItem
                        v-for="filter in filters"
                        :key="`listing-settings-header__filtres-${filter.code}`"
                        :label="filter.label"
                        @click="onFilterSelected(filter)"
                    />
                </template>
            </CompassMenu>
        </div>

        <div class="listing-settings-header__utils-container">
            <span
                v-if="displayTotalResults"
                class="typographies-paragraph-medium-bold listing-settings-header__utils-total-results"
            >
                {{ textTotalResults }}
            </span>

            <CompassTooltip
                v-if="refreshable"
                :content="$t.__('common.actions.refresh')"
                placement="top-end"
            >
                <CompassIconButton type="secondary" icon="reload" @click="handleRefresh" />
            </CompassTooltip>

            <ListingSettings
                v-if="enableColumnsSettings"
                :searchableColumns="searchableColumns"
                @updateColumns="updateColumns"
            />
        </div>
    </div>
</template>

<script>
import ListingSettings from 'vcomponents/common/listing/settings/ListingSettings.vue';

export default {
    components: {
        ListingSettings,
    },
    props: {
        displayTotalResults: {
            type: Boolean,
            default: false,
        },
        totalResults: {
            type: Number,
            default: 0,
        },
        filters: {
            type: Array,
            default: () => [],
        },
        searchable: {
            type: Boolean,
            default: false,
        },
        enableColumnsSettings: {
            type: Boolean,
            default: false,
        },
        searchableColumns: {
            type: Boolean,
            default: true,
        },
        searchValue: {
            type: String,
            default: '',
        },
        refreshable: {
            type: Boolean,
            default: false,
        },
        customTotalResultsLabel: {
            type: String,
            default: '',
        },
    },
    computed: {
        hasActiveFilters() {
            return this.filters.some((filter) => filter.isActive);
        },
        textTotalResults() {
            if (this.customTotalResultsLabel) {
                return this.customTotalResultsLabel;
            }

            if (this.totalResults === 0) {
                return this.$t.__('listing.settings.ListingSettingsHeader.no_results');
            }

            return this.$t.n__(
                'listing.settings.ListingSettingsHeader.total_results',
                null,
                this.totalResults,
                this.totalResults,
            );
        },
    },
    methods: {
        onFilterSelected(filterSelected) {
            setTimeout(() => {
                this.$emit('selectFilter', filterSelected);
            }, 0);
        },
        updateColumns(newColumns) {
            this.$emit('updateColumns', newColumns);
        },
        updateSearchValue(newValue) {
            this.$emit('search', newValue);
        },
        handleRefresh() {
            this.$emit('refresh');
        },
    },
};
</script>

<style lang="scss" scoped>
.listing-settings-header {
    display: flex;
    justify-content: space-between;
    padding: var(--spacing-3) 0;
    background-color: var(--colors-common-background-default);

    &__divider {
        width: 1px;
        height: 32px;
        background-color: var(--colors-common-border-lighter);
        margin: 0 var(--spacing-3);
    }

    &__filters {
        &-container {
            display: flex;
            align-items: center;
            gap: var(--spacing-3);
        }

        &-select {
            flex-shrink: 0;
            position: relative;
        }

        &-tags {
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            flex-wrap: wrap;
            gap: var(--spacing-3);
        }
    }

    &__utils {
        &-container {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: var(--spacing-3);
            flex-shrink: 0;
            margin-left: auto;
        }

        &-search {
            min-width: 240px;
        }
        &-total-results {
            color: var(--colors-common-text-subtle-default);
        }
    }
}
</style>

<style lang="scss">
#compass-menu-container {
    .listing-settings-header__filters-select-dropdown {
        z-index: 3;
    }
}
</style>
